import { array, option, record } from 'fp-ts';
import { identity, pipe } from 'fp-ts/function';
import {
    CancellationPolicy,
    EnterpriseSettlementRule,
    PaymentPolicy,
    RateGroupWithPaymentPolicies,
} from '@mews-commander/data-access';
import { keyBy } from '@mews/fp';
import { Identifiable } from '@mews/std';
import { WithCancellationPolicies, WithPaymentPolicies } from '../types';

export const mapToRecordValue = <V>(collection: Record<string, V>) =>
    array.filterMap((id: string) => pipe(collection, record.lookup(id)));

interface PaymentPolicyMappers {
    settlementRules: Array<EnterpriseSettlementRule>;
    cancellationPolicies: Array<CancellationPolicy>;
}

export const createPaymentPolicyMapper = ({ settlementRules, cancellationPolicies }: PaymentPolicyMappers) => {
    const keyedSettlementRules = keyBy('id', settlementRules);
    const keyedCancellationPolicies = keyBy('id', cancellationPolicies);

    const mapToSettlementRules = mapToRecordValue(keyedSettlementRules);
    const mapToCancellationPolicies = mapToRecordValue(keyedCancellationPolicies);

    const mapToPaymentPolicy = (
        paymentPolicy: PaymentPolicy,
        index: number | string = 'default'
    ): Identifiable<number | string> & WithPaymentPolicies & WithCancellationPolicies => ({
        id: index,
        settlementRules: pipe(paymentPolicy.settlementRuleIds, mapToSettlementRules),
        cancellationPolicies: pipe(paymentPolicy.cancellationPolicyIds, mapToCancellationPolicies),
    });

    return ({ paymentPolicy, paymentPolicyOverrides, ...other }: RateGroupWithPaymentPolicies) => ({
        ...other,
        defaultPolicy: paymentPolicy ? mapToPaymentPolicy(paymentPolicy) : undefined,
        policyExceptions: pipe(
            paymentPolicyOverrides,
            option.fromNullable,
            option.map(
                array.mapWithIndex((index, { timeUnitIntervals, rateIds, guestExperienceOptions, ...rest }) => ({
                    timeUnitIntervals,
                    rateIds,
                    // not correctly annotated, guestExperienceOptions can be undefined, remove this comment when it's fixed
                    guestExperienceOptions: guestExperienceOptions ?? [],
                    policyId: rest.id,
                    ...mapToPaymentPolicy(rest, index),
                }))
            ),
            option.match(() => [], identity)
        ),
    });
};
